import type { SlotTotals } from '../hours/calculateSlots'
import { parseFlexibleHours } from '../hours/parseFlexibleHours'

const MINUTES_PER_HOUR = 60

/**
 * Flat bonus applied when the driver has performed a truck wash during
 * the mission. Exported so that UI and reporting layers can display the
 * same constant without duplicating the magic number.
 */
export const WASHING_BONUS_MINUTES = 30

/**
 * Bonus inputs entered by the driver on top of the time slots.
 *
 * - `washing` is a boolean checkbox.
 * - `travel` is a flexible time string (e.g. "1h30", "1,5", "0:45").
 *   It is parsed with {@link parseFlexibleHours}; invalid or negative
 *   values resolve to 0 minutes (no throw).
 */
export type BonusInput = {
  readonly washing: boolean
  readonly travel: string
}

/**
 * {@link SlotTotals} augmented with per-bonus breakdown.
 *
 * Invariant: `totalMinutes === dayMinutes + nightMinutes + bonusMinutes`.
 *
 * Bonuses are intentionally *not* distributed into `dayMinutes` /
 * `nightMinutes`: a truck wash or a travel leg has no natural position
 * on the day/night scale, so they only grow the global total.
 */
export type BonusTotals = SlotTotals & {
  readonly washingMinutes: number
  readonly travelMinutes: number
  readonly bonusMinutes: number
}

/**
 * Adds the truck-wash and travel bonuses on top of a set of slot
 * totals previously produced by `calculateSlots`.
 *
 * Behaviour:
 * - `washingMinutes = opts.washing ? WASHING_BONUS_MINUTES : 0`.
 * - `travelMinutes = round(parseFlexibleHours(opts.travel) * 60)`. The
 *   round-trip via `parseFlexibleHours` fixes the V1 PHP hack that
 *   could only encode half-hours for travel time.
 * - `bonusMinutes = washingMinutes + travelMinutes`.
 * - `totalMinutes` is incremented by `bonusMinutes`; `dayMinutes` and
 *   `nightMinutes` are passed through unchanged.
 *
 * The function is pure, trusts its typed inputs, and never throws —
 * any malformed `travel` value resolves to 0 via `parseFlexibleHours`.
 */
export function addBonuses(
  totals: SlotTotals,
  opts: BonusInput,
): BonusTotals {
  const washingMinutes = opts.washing ? WASHING_BONUS_MINUTES : 0
  const travelMinutes = Math.round(parseFlexibleHours(opts.travel) * MINUTES_PER_HOUR)
  const bonusMinutes = washingMinutes + travelMinutes

  return {
    totalMinutes: totals.totalMinutes + bonusMinutes,
    dayMinutes: totals.dayMinutes,
    nightMinutes: totals.nightMinutes,
    washingMinutes,
    travelMinutes,
    bonusMinutes,
  }
}
